import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Send, Building2, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Spinner, EmptyState } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ReportCommentButton } from "@/components/ReportCommentButton";

export default function PostDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [comment, setComment] = useState("");
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";

  const remove = async (kind, cid, msg) => {
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/admin/community/${kind}/${cid}`);
      toast.success("Deleted");
      if (kind === "posts") navigate("/community"); else load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const load = () => api.get(`/community/posts/${id}`).then((r) => setP(r.data)).catch(() => setNotFound(true));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const addComment = async (e) => {
    e.preventDefault();
    if (!user) { toast("Log in to reply"); return; }
    try {
      await api.post(`/community/posts/${id}/comments`, { body: comment });
      setComment(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (notFound) return <div className="propora-container py-16"><EmptyState title="Discussion not found" /></div>;
  if (!p) return <Spinner />;

  return (
    <div className="propora-container py-10 max-w-3xl" data-testid="post-detail-page">
      <Link to="/community" className="inline-flex items-center gap-1.5 text-sm text-[#4A5568] hover:text-[#007F83] mb-6"><ArrowLeft size={16} /> Back to community</Link>
      <div className="bg-white rounded-3xl border border-[#E3E8E8] p-7 shadow-soft">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-[#007F83] bg-[#E8F5F1] px-2 py-0.5 rounded">{p.topic}</span>
          {p.location && <span className="text-xs text-[#4A5568] inline-flex items-center gap-1"><MapPin size={12} /> {p.location}</span>}
        </div>
        <h1 className="font-display font-extrabold text-3xl text-[#0B1733]">{p.title}</h1>
        <div className="mt-2 flex items-center justify-between gap-3">
          <p className="text-sm text-[#4A5568]">by <b className="text-[#0B1733]">{p.author}</b></p>
          {isAdmin && <Button size="sm" variant="outline" onClick={() => remove("posts", p.id, "Delete this post and all its comments?")} data-testid="delete-post-btn" className="rounded-full text-red-600 border-red-200 hover:bg-red-50"><Trash2 size={14} className="mr-1" /> Delete post</Button>}
        </div>
        <p className="mt-5 text-[#4A5568] leading-relaxed whitespace-pre-wrap">{p.body}</p>
      </div>

      <h3 className="font-display font-bold text-xl text-[#0B1733] mt-8 mb-4">{p.comments?.length || 0} replies</h3>
      <div className="space-y-3">
        {p.comments?.map((c) => (
          <div key={c.id} className="bg-white rounded-2xl border border-[#E3E8E8] p-5" data-testid={`comment-${c.id}`}>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm text-[#0B1733]">{c.author}</span>
              {c.is_company && <span className="inline-flex items-center gap-1 text-[11px] bg-[#E8F5F1] text-[#007F83] px-2 py-0.5 rounded-full font-semibold"><Building2 size={11} /> Company</span>}
              {user && !isAdmin && <ReportCommentButton commentId={c.id} />}
              {isAdmin && <button onClick={() => remove("comments", c.id, "Delete this comment?")} data-testid={`delete-comment-${c.id}`} className="ml-auto inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700"><Trash2 size={13} /> Delete</button>}
            </div>
            <p className="mt-1.5 text-sm text-[#4A5568] whitespace-pre-wrap">{c.body}</p>
          </div>
        ))}
      </div>

      <form onSubmit={addComment} className="mt-6 bg-white rounded-2xl border border-[#E3E8E8] p-5">
        <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder={user ? "Write a reply..." : "Log in to reply"} rows={3} required data-testid="comment-input" />
        <div className="mt-3 flex justify-end">
          <Button type="submit" data-testid="comment-submit-btn" className="rounded-full bg-[#007F83] hover:bg-[#006B70] text-white">Reply <Send size={15} className="ml-1.5" /></Button>
        </div>
      </form>
    </div>
  );
}
